// @greenlight-spec: 07-source-control-pipeline.md §Re-install semantics
// @greenlight-spec: 12-azure-installation.md

import type { GithubAppManifestHandshake } from '@greenlight/shared';

/**
 * Submit a GitHub App manifest to GitHub via a real top-level form POST. `fetch`
 * cannot perform GitHub's cross-origin manifest handshake (it must be a
 * navigation), so we build a detached form and submit it. The `state` is already
 * embedded in `action`'s query string; the form only carries the `manifest`
 * field. `target="_blank"` opens the handshake in a new tab rather than
 * navigating the caller away — the wizard/Settings tab that submitted this form
 * stays put; the new tab carries the GitHub create round trip, and the
 * `/install/github-app/callback` 302s that tab straight to GitHub's install URL.
 *
 * Shared by the install wizard's GitHub App step and the Settings → Source
 * Control "Re-create App" action — both hand the server-issued manifest back to
 * GitHub the same way (docs/07 §Re-install semantics).
 */
export function submitManifestForm(action: string, manifest: string): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.target = '_blank';
  form.rel = 'noreferrer';
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'manifest';
  input.value = manifest;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
  form.remove();
}

/**
 * Submits the manifest handshake when present and reports whether it did.
 * `beginGithubApp` omits `manifest`/`action` when the App already exists
 * (install-link fallback instead); `recreateGithubApp` always has them. One
 * guard both the wizard and Settings call, so the two surfaces can't drift on
 * how a manifest result is handled.
 */
export function submitManifestIfPresent(result: Partial<GithubAppManifestHandshake>): boolean {
  if (!result.manifest || !result.action) return false;
  submitManifestForm(result.action, result.manifest);
  return true;
}
